import type { FlatSlot } from '../../../atoms/slot';
import type { StructuredText } from '../../../types/annotation';
import { INLINE_TAG_TYPE } from '../../../types/annotation-label';
const PUNCTUATION_RE = /[,.'";:!?]/g;

/**
 * Strips common punctuation so that "wait," matches "wait" and
 * "don't" matches "dont" when comparing overlap vs transcript words.
 */
export const normalizeWord = (word: string): string => word.replace(PUNCTUATION_RE, '');
const buildOverlapWordMap = (flatSlots: FlatSlot[]): Map<string, string[]> => {
  const overlapWordsByTargetSlot = new Map<string, string[]>();
  for (const flatSlot of flatSlots) {
    if (flatSlot.isDraft) continue;
    const {
      slot
    } = flatSlot;
    if (!slot.flattenWords || slot.flattenWords.length === 0) continue;
    for (const word of slot.flattenWords) {
      if (word.type === INLINE_TAG_TYPE.SPEAKER_OVERLAP) {
        const targetSlotId = word.slotId;
        if (!overlapWordsByTargetSlot.has(targetSlotId)) {
          overlapWordsByTargetSlot.set(targetSlotId, []);
        }
        overlapWordsByTargetSlot.get(targetSlotId)!.push(normalizeWord(word.transcript));
      }
    }
  }
  return overlapWordsByTargetSlot;
};

/**
 * @param attributeSpeakerToSourceSlot — When true (WER export), every word emitted from a slot's
 *   `flattenWords` is tagged with that slot's parent annotation id so overlap spans group with the
 *   speaker whose segment they were marked on. When false (UI), overlap tokens keep their stored ids
 *   for coloring/navigation.
 */
const emitDeduplicatedWords = (flatSlots: FlatSlot[], overlapWordsByTargetSlot: Map<string, string[]>, attributeSpeakerToSourceSlot: boolean): StructuredText[] => {
  const result: StructuredText[] = [];
  for (const flatSlot of flatSlots) {
    if (flatSlot.isDraft) continue;
    const {
      slot
    } = flatSlot;
    if (slot.transcript.trim().length === 0) continue;
    const hasFlattenWords = slot.flattenWords && slot.flattenWords.length > 0;
    const mapWordForOutput = (word: StructuredText): StructuredText => attributeSpeakerToSourceSlot ? {
      ...word,
      annotationId: flatSlot.annotationId,
      slotId: flatSlot.slotId
    } : word;
    if (hasFlattenWords) {
      const overlapWordsToSkip = overlapWordsByTargetSlot.get(slot.id);
      let skipIndex = 0;
      for (const word of slot.flattenWords) {
        if (word.type === INLINE_TAG_TYPE.SPEAKER_OVERLAP) {
          result.push(mapWordForOutput(word));
          continue;
        }
        if (overlapWordsToSkip && skipIndex < overlapWordsToSkip.length && normalizeWord(word.transcript) === overlapWordsToSkip[skipIndex]) {
          skipIndex++;
          continue;
        }
        result.push(mapWordForOutput(word));
      }
    } else {
      const words = slot.transcript.split(' ').filter(w => w.length > 0);
      const overlapWordsToSkip = overlapWordsByTargetSlot.get(slot.id);
      if (overlapWordsToSkip && overlapWordsToSkip.length > 0) {
        let skipIndex = 0;
        for (const word of words) {
          if (skipIndex < overlapWordsToSkip.length && normalizeWord(word) === overlapWordsToSkip[skipIndex]) {
            skipIndex++;
          } else {
            result.push({
              transcript: word,
              type: 'NORMAL',
              annotationId: flatSlot.annotationId,
              slotId: slot.id
            });
          }
        }
      } else {
        for (const word of words) {
          result.push({
            transcript: word,
            type: 'NORMAL',
            annotationId: flatSlot.annotationId,
            slotId: slot.id
          });
        }
      }
    }
  }
  return result;
};

/**
 * Builds a deduplicated global structured text from all flat slots.
 *
 * Strategy (two-pass):
 * 1. **Pre-scan**: Walk every slot's `flattenWords` to collect all
 *    SPEAKER_OVERLAP words keyed by their target slotId. This builds the
 *    full overlap map upfront so it works regardless of slot ordering.
 * 2. **Emit**: Process slots in time order (already sorted by flatSlotsAtom).
 *    - Slots with `flattenWords`: emit words in order; emit SPEAKER_OVERLAP as-is, and
 *      skip NORMAL words on the *target* slot that duplicate overlap (same as the
 *      no-flattenWords path) so overlapping phrases are not repeated.
 *    - Slots without `flattenWords`: fall back to raw transcript, skipping
 *      any words that match the overlap map (punctuation-insensitive).
 */
export const getDeduplicatedGlobalText = (flatSlots: FlatSlot[]): StructuredText[] => {
  const overlapWordsByTargetSlot = buildOverlapWordMap(flatSlots);
  return emitDeduplicatedWords(flatSlots, overlapWordsByTargetSlot, false);
};

/**
 * Same deduplication as {@link getDeduplicatedGlobalText}, but overlap words are attributed to the
 * **source** slot's annotation for downstream WER-style speaker grouping.
 */
export const getDeduplicatedGlobalTextForWer = (flatSlots: FlatSlot[]): StructuredText[] => {
  const overlapWordsByTargetSlot = buildOverlapWordMap(flatSlots);
  return emitDeduplicatedWords(flatSlots, overlapWordsByTargetSlot, true);
};